import React, { useMemo } from "react";
import InvoiceUnderConstruction from "assets/images/InvoiceUnderConstruction.png";
import InvoiceCanceled from "assets/images/InvoiceCanceled.png";
import { formatCashFull } from "utils/formatCash";
import { titleCase } from "title-case";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Link,
  Skeleton,
  Typography,
} from "@mui/material";
import { ListItemIcon, ListItemIconWrapper, StatusGlyph } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import useGetInvoiceStatusDetails from "hooks/useGetInvoiceStatusDetails";
import { InvoiceStatus } from "enums/InvoiceStatus";

interface StatusContent {
  title: string;
  description?: string;
  /** The tile's tone and the status label its glyph is looked up by. */
  tone?: ListItemIconWrapper;
  glyph?: string;
  /** An illustration shown in place of the tile. */
  image?: JSX.Element;
}

const STATUS_CONTENT_MAP: Record<string, StatusContent> = {
  succeeded: {
    title: "PaymentSucceededTitle",
    description: "PaymentSucceededDescription",
    tone: "success",
    glyph: "paid",
  },
  processing: {
    title: "PaymentProcessingTitle",
    description: "PaymentProcessingDescription",
    tone: "info",
    glyph: "processing",
  },
  verify_bank: {
    title: "VerifyBankTitle",
    description: "VerifyBankDescription",
    tone: "warning",
    glyph: "pending",
  },
  requires_payment_method: {
    title: "PaymentFailedTitle",
    description: "PaymentFailedDescription",
    tone: "danger",
    glyph: "failed",
  },
  error: {
    title: "PaymentErrorTitle",
    description: "PaymentErrorDescription",
    tone: "danger",
    glyph: "failed",
  },
  canceled: {
    title: "InvoiceCanceledTitle",
    description: "InvoiceCanceledDescription",
    image: <img src={InvoiceCanceled} alt="Invoice Canceled" />,
  },
  couldNotLoad: {
    title: "InvoiceCouldNotLoadTitle",
    description: "InvoiceCouldNotLoadDescription",
    tone: "danger",
    glyph: "failed",
  },
  invalidUrl: {
    title: "Invalid URL",
    description:
      "The invoice link is missing required information. Please check the URL or contact support.",
    tone: "danger",
    glyph: "failed",
  },
  // catchall status for any other status not explicitly defined
  otherStatus: {
    title: "InvoiceInProgressTitle",
    description: "InvoiceInProgressDescription",
    image: <img src={InvoiceUnderConstruction} alt="Invoice In Progress" />,
  },
};

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Inline wrap={false} justify="between" gap="var(--space-3)">
    <Typography variant="body1" sx={{ color: "var(--color-text-secondary)" }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ textAlign: "right" }}>
      {value}
    </Typography>
  </Inline>
);

interface PaymentDetailsBoxProps {
  paymentIntentStatus: string;
  paymentMethodDetails: any;
  invoiceData: any;
  error?: { message?: string; code?: string; doc_url?: string };
  verificationUrl?: string;
  onTryAgain?: () => void;
}

export default function PaymentDetailsBox({
  paymentIntentStatus: status = "otherStatus",
  paymentMethodDetails,
  invoiceData,
  error,
  verificationUrl,
  onTryAgain,
}: PaymentDetailsBoxProps) {
  const { t } = useTranslation();
  const content = STATUS_CONTENT_MAP[status];

  const { isPaid, isPaymentProcessing } = useGetInvoiceStatusDetails(
    invoiceData?.status as InvoiceStatus,
  );

  const canShowOrderDetails =
    (paymentMethodDetails || invoiceData) &&
    !error &&
    (isPaid || isPaymentProcessing);

  const renderPaymentMethodDetails = useMemo(() => {
    if (!paymentMethodDetails) return null;

    const { brand, last4, bank_name, name } = paymentMethodDetails.details;
    const rows = [];

    if (brand && last4) {
      rows.push(
        <DetailRow
          key="card"
          label={t("Payment Method")}
          value={`${titleCase(brand)} ***** ${last4}`}
        />,
      );
    }

    if (bank_name) {
      rows.push(
        <DetailRow key="bank" label={t("Bank Account")} value={bank_name} />,
      );
    }

    if (last4 && bank_name) {
      rows.push(
        <DetailRow
          key="bank-last4"
          label={t("Last 4")}
          value={`***** ${last4}`}
        />,
      );
    }

    if (name) {
      rows.push(
        <DetailRow key="name" label={t("Account Name")} value={name} />,
      );
    }

    return <>{rows}</>;
  }, [paymentMethodDetails, t]);

  if (status === "loadingInvoiceOrPayment") {
    // The card it stands in for, drawn as a skeleton beside the invoice card's.
    return (
      <Card aria-busy="true" aria-label={t("LoadingInvoiceOrPaymentTitle")}>
        <CardContent>
          <Stack gap="var(--space-4)" sx={{ alignItems: "center" }}>
            <Skeleton variant="rounded" width={36} height={36} />
            <Skeleton variant="text" width="55%" height={32} />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="rounded" width="100%" height={38} />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Stack
          gap="var(--space-5)"
          sx={{ alignItems: "center", textAlign: "center" }}
        >
          {content?.image ??
            (content?.tone && content.glyph && (
              <ListItemIcon wrapper={content.tone} sx={{ minWidth: 0 }}>
                <StatusGlyph status={content.glyph} size={20} />
              </ListItemIcon>
            ))}

          <Stack gap="var(--space-2)" sx={{ alignItems: "center" }}>
            <Typography variant="h3">{t(content?.title)}</Typography>
            {content?.description && (
              <Typography
                variant="body1"
                sx={{ color: "var(--color-text-secondary)" }}
              >
                {t(content.description)}
              </Typography>
            )}
          </Stack>

          {(status === "requires_payment_method" || status === "error") &&
            error?.message && (
              <>
                <Typography variant="body1">
                  {t(error.message)}
                  {error.doc_url && (
                    <Typography component="span" variant="inherit">
                      {" "}
                      <Link
                        href={error.doc_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="always"
                      >
                        {t("Learn more")}
                      </Link>
                    </Typography>
                  )}
                </Typography>
                {onTryAgain && (
                  <Button variant="primary-filled" onClick={onTryAgain}>
                    {t("Try another payment method")}
                  </Button>
                )}
              </>
            )}

          {status === "verify_bank" && verificationUrl && (
            <Button
              variant="primary-filled"
              onClick={() =>
                window.open(verificationUrl, "_blank", "noopener,noreferrer")
              }
            >
              {t("Verify bank account")}
            </Button>
          )}

          {canShowOrderDetails && (
            <Box sx={{ width: "100%", textAlign: "left" }}>
              <Divider sx={{ marginBottom: "var(--space-5)" }} />
              <Stack gap="var(--space-3)">
                <Typography variant="subtitle1">
                  {t("Order Details")}
                </Typography>
                <DetailRow
                  label={t("Invoice")}
                  value={invoiceData?.invoiceNumber}
                />
                {invoiceData?.purchaseOrderId && (
                  <DetailRow
                    label={t("Purchase Order")}
                    value={invoiceData?.purchaseOrderId}
                  />
                )}
                <DetailRow
                  label={t("Total")}
                  value={formatCashFull(
                    paymentMethodDetails?.amount ?? invoiceData?.total,
                    paymentMethodDetails?.currency ?? invoiceData?.currency,
                  )}
                />
                {renderPaymentMethodDetails}
              </Stack>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
